/**
 * InteractiveObject — base class for world objects that players interact with.
 *
 * Babylon.js port of the Phaser InteractiveObject. Each interactive has:
 * - A state machine (idle / active / solved / hidden)
 * - Reveal/conceal for Aura Sight (toggles visibility + emissive glow)
 * - Highlight on player proximity (emissive outline pulse)
 * - Solve animation (flash effect)
 * - Uses Babylon.js meshes and StandardMaterial / PBRMaterial
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Animation } from "@babylonjs/core/Animations/animation";
import { HighlightLayer } from "@babylonjs/core/Layers/highlightLayer";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../../levels/PlaceholderTag";
import { setMeshVisibility, supportsMeshVisibilityAlpha } from "../../core/meshVisibility";
import { isLowFiMode } from "../../core/lowFiMode";

/**
 * Shared proximity-highlight layer state, cached per scene. `count`
 * tracks how many interactives currently hold a mesh in the layer so
 * we can disable it at zero members — an enabled HighlightLayer has
 * per-frame render-target cost even with nothing highlighted, which
 * matters on L1 (SeizedCrane calls highlight() every readability tick
 * on the fps-tightest level).
 */
interface ProximityHighlightState {
  layer: HighlightLayer;
  count: number;
}

const PROXIMITY_HIGHLIGHT_KEY = "__proximityHighlight";

/**
 * Lazily create (and cache in scene.metadata) ONE shared proximity
 * HighlightLayer for all interactives in a scene. Returns null — and
 * caches the null — when:
 *   - low-fi/software-GL mode is active (the Q-017 shader-churn class:
 *     a highlight layer's blur passes force shader work SwiftShader
 *     can't afford), or
 *   - layer creation throws (NullEngine/unit-test environments).
 *
 * Deliberately NOT BaseLevel's shared "sharedHighlight" layer:
 * AuraSight force-disables that one on deactivate (AuraSight.ts), which
 * would kill proximity outlines the moment the aura drops.
 */
function getProximityHighlightState(scene: Scene): ProximityHighlightState | null {
  const metadata = (scene.metadata ??= {}) as Record<string, unknown>;
  if (PROXIMITY_HIGHLIGHT_KEY in metadata) {
    return metadata[PROXIMITY_HIGHLIGHT_KEY] as ProximityHighlightState | null;
  }
  if (isLowFiMode()) {
    metadata[PROXIMITY_HIGHLIGHT_KEY] = null;
    return null;
  }
  try {
    const layer = new HighlightLayer("proximityHighlight", scene, {
      blurHorizontalSize: 0.8,
      blurVerticalSize: 0.8,
    });
    layer.isEnabled = false;
    const state: ProximityHighlightState = { layer, count: 0 };
    metadata[PROXIMITY_HIGHLIGHT_KEY] = state;
    return state;
  } catch {
    metadata[PROXIMITY_HIGHLIGHT_KEY] = null;
    return null;
  }
}

export type InteractiveState = "idle" | "active" | "solved" | "hidden";
export type ReadabilityState = "suppressed" | "idle" | "hint" | "focus" | "active" | "solved";

export interface InteractiveConfig {
  /** Unique identifier for this object instance. */
  key: string;
  /** World position. */
  position: Vector3;
  /** Dimensions (width, height, depth) in world units. */
  size: Vector3;
  /** If true, only visible when Aura Sight is active. */
  hidden?: boolean;
  /** Which ability can interact (e.g., 'turnTableTouch'). */
  requiredAbility?: string;
  /** Display label for UI hints. */
  label?: string;
}

export abstract class InteractiveObject {
  public readonly key: string;
  public state: InteractiveState;
  public readonly config: InteractiveConfig;

  /** Root mesh (container for child meshes). */
  public rootMesh: Mesh;

  protected scene: Scene;
  protected material: StandardMaterial;
  private isHighlighted = false;
  /** Mesh currently registered in the shared proximity HighlightLayer. */
  private proximityHighlightMesh: Mesh | null = null;
  private modelInstance: InstantiatedModel | null = null;
  private readabilityState: ReadabilityState = "idle";

  /**
   * Model paths whose load failure has already produced a player-facing
   * toast — one toast per distinct path per session, not one per
   * instance (a level with a dozen pressure plates should not stack a
   * dozen identical toasts). Static so it spans instances; reset via
   * {@link resetForTests} because the singleton gameEvents bleeds
   * across NullEngine test workers.
   */
  private static reportedModelFailures = new Set<string>();

  /** Test hook: clear the once-per-path load-failure toast dedupe. */
  static resetForTests(): void {
    InteractiveObject.reportedModelFailures.clear();
  }

  constructor(scene: Scene, config: InteractiveConfig) {
    this.scene = scene;
    this.key = config.key;
    this.config = config;
    this.state = config.hidden ? "hidden" : "idle";

    // Create an invisible root transform node as a box mesh with zero rendering
    this.rootMesh = MeshBuilder.CreateBox(
      `interactive_${config.key}_root`,
      { width: 0.01, height: 0.01, depth: 0.01 },
      scene
    );
    this.rootMesh.position = config.position.clone();
    this.rootMesh.isVisible = false;
    // Tag procedural fallback root as placeholder. If a glTF model loads
    // successfully via `loadModelOverride`, the model's root will be
    // tagged "final" and override this for art-debt audits.
    tagAs(this.rootMesh, "placeholder");

    // Create default material
    this.material = new StandardMaterial(`interactive_${config.key}_mat`, scene);
    this.material.specularColor = Color3.Black();
    this.updateMaterialForState();

    // Hidden objects start invisible
    if (this.state === "hidden") {
      this.rootMesh.visibility = 0;
      this.setChildVisibility(0);
    }
  }

  /**
   * Override in subclasses to define interaction behavior.
   * @returns true if the interaction was consumed.
   */
  onInteract(_abilityId: string): boolean {
    return false;
  }

  getHighlightTarget(): AbstractMesh {
    const visibleChildren = this.rootMesh
      .getChildMeshes()
      .filter((mesh) => mesh.isVisible && mesh.visibility > 0.05);
    if (visibleChildren.length === 0) {
      return this.rootMesh;
    }

    let largest = visibleChildren[0];
    let largestVolume = 0;
    for (const mesh of visibleChildren) {
      const extents = mesh.getBoundingInfo().boundingBox.extendSizeWorld;
      const volume = extents.x * extents.y * extents.z;
      if (volume > largestVolume) {
        largest = mesh;
        largestVolume = volume;
      }
    }
    return largest;
  }

  setReadabilityState(state: ReadabilityState): void {
    this.readabilityState = state;
    this.applyReadabilityState();
  }

  /**
   * Reveal a hidden object (called by Aura Sight activation).
   */
  reveal(): void {
    if (this.state !== "hidden") return;
    this.state = "idle";
    this.updateMaterialForState();
    this.rootMesh.visibility = 1;

    // Animate visibility from 0 to 1
    this.animateVisibility(0, 1, 600);

    // Add emissive glow during reveal
    this.material.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(0.3);

    gameEvents.emit(GameEvents.INTERACTIVE_REVEALED, this.key);
  }

  /**
   * Conceal the object (called when Aura Sight deactivates).
   */
  conceal(): void {
    if (!this.config.hidden || this.state !== "idle") return;
    this.state = "hidden";
    this.rootMesh.visibility = 0;

    // Animate visibility from 1 to 0
    this.animateVisibility(1, 0, 400);

    // Remove emissive glow
    this.material.emissiveColor = Color3.Black();

    gameEvents.emit(GameEvents.INTERACTIVE_CONCEALED, this.key);
  }

  /**
   * Show highlight effect when player is nearby and can interact.
   *
   * Uses the shared proximity HighlightLayer (brass outline) instead of
   * the old wireframe box, which read as debug geometry (Q-036d). On
   * low-fi/software-GL or when no layer is available (NullEngine), this
   * is a visual no-op — the readability "focus" emissive from
   * applyReadabilityState carries the proximity cue instead.
   */
  highlight(): void {
    if (this.isHighlighted || this.state === "hidden") return;
    this.isHighlighted = true;

    const shared = getProximityHighlightState(this.scene);
    if (!shared) return;

    const target = this.getHighlightTarget();
    if (!(target instanceof Mesh)) return;

    shared.layer.addMesh(target, Color3.FromHexString(COLORS.BRASS));
    this.proximityHighlightMesh = target;
    shared.count += 1;
    shared.layer.isEnabled = true;
  }

  /**
   * Remove highlight effect. Disables the shared layer when the last
   * member leaves so an idle HighlightLayer never ticks per frame.
   */
  unhighlight(): void {
    if (!this.isHighlighted) return;
    this.isHighlighted = false;

    if (!this.proximityHighlightMesh) return;
    const shared = getProximityHighlightState(this.scene);
    if (shared) {
      shared.layer.removeMesh(this.proximityHighlightMesh);
      shared.count = Math.max(0, shared.count - 1);
      if (shared.count === 0) {
        shared.layer.isEnabled = false;
      }
    }
    this.proximityHighlightMesh = null;
  }

  /**
   * Mark as solved — permanent state. Plays a flash animation.
   */
  solve(): void {
    this.state = "solved";
    this.updateMaterialForState();

    // Flash green emissive then fade
    this.material.emissiveColor = Color3.FromHexString(COLORS.EMERALD);

    // Create a simple animation that flashes the emissive
    const flashAnim = new Animation(
      `flash_${this.key}`,
      "material.emissiveColor",
      30,
      Animation.ANIMATIONTYPE_COLOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    flashAnim.setKeys([
      { frame: 0, value: Color3.FromHexString(COLORS.EMERALD) },
      { frame: 5, value: Color3.FromHexString(COLORS.EMERALD).scale(0.2) },
      { frame: 10, value: Color3.FromHexString(COLORS.EMERALD) },
      { frame: 15, value: Color3.FromHexString(COLORS.EMERALD).scale(0.2) },
      { frame: 20, value: Color3.FromHexString(COLORS.EMERALD) },
      { frame: 30, value: Color3.FromHexString(COLORS.EMERALD).scale(0.3) },
    ]);

    // Apply to all child meshes that use this material
    const meshes = this.rootMesh.getChildMeshes();
    for (const mesh of meshes) {
      if (mesh.material === this.material) {
        mesh.animations.push(flashAnim);
        this.scene.beginAnimation(mesh, 0, 30, false);
      }
    }

    gameEvents.emit(GameEvents.INTERACTIVE_SOLVED, this.key);
  }

  /**
   * Check distance from a world position (used for proximity highlight).
   */
  distanceTo(worldPos: Vector3): number {
    return Vector3.Distance(this.rootMesh.position, worldPos);
  }

  /**
   * Dispose all meshes and materials.
   */
  dispose(): void {
    // Release proximity-layer membership (and its enable bookkeeping)
    // before tearing down meshes.
    this.unhighlight();
    if (this.modelInstance) {
      this.modelInstance.dispose();
      this.modelInstance = null;
    }
    this.material.dispose();
    this.rootMesh.dispose();
  }

  /**
   * Load a 3D model to replace procedural placeholder geometry.
   * The procedural mesh is hidden but kept for physics collisions.
   * Falls back silently to procedural mesh if model fails to load.
   *
   * Routes through {@link getAssetManager} so repeated loads of the same
   * model (e.g. many pressure plates sharing `pressure_plate.glb`) only
   * download the template once and then instantiate fresh clones.
   */
  protected async loadModelOverride(
    modelPath: string,
    options?: { sharedInstances?: boolean; requireGeometry?: boolean },
  ): Promise<void> {
    try {
      const instance = options?.sharedInstances
        ? await getAssetManager().instantiateSharedModel(this.scene, modelPath)
        : await getAssetManager().instantiateModel(this.scene, modelPath);
      if (this.rootMesh.isDisposed() || this.scene.isDisposed) {
        instance.dispose();
        return;
      }
      if (options?.requireGeometry && !instance.meshes.some(m => m.getTotalVertices() > 0)) {
        instance.dispose(); throw new Error("Optional interactive model contains no geometry");
      }
      this.modelInstance = instance;

      // Parent loaded meshes to rootMesh for positioning. Roots from
      // instantiateModelsToScene are top-level clones with no parent.
      const instanceMeshes = new Set<AbstractMesh>(instance.meshes);
      for (const root of instance.rootNodes) {
        if (!root.parent) {
          root.parent = this.rootMesh;
        }
        // Tag the instantiated glTF root as production art so audits
        // can distinguish it from the procedural fallback root above.
        tagAs(root, "final");
      }
      // Also tag every loaded mesh so deep audits (per-mesh) see "final"
      // rather than "untagged" descendants of an instantiated model.
      for (const mesh of instance.meshes) {
        tagAs(mesh, "final");
      }

      // Hide the procedural placeholder meshes (keep for physics)
      for (const child of this.rootMesh.getChildMeshes()) {
        if (!instanceMeshes.has(child)) {
          child.isVisible = false;
        }
      }

      // Model overrides load asynchronously, often after the constructor's
      // hidden-state visibility has already been applied to procedural meshes.
      // Re-apply the current interactive visibility so GLB art cannot leak
      // into the scene before Aura Sight reveals it.
      this.setChildVisibility(this.state === "hidden" ? 0 : 1);

      // Subclass hook: let CrystalReceiver (and future subclasses) reach
      // into the freshly loaded GLB to grab material refs for runtime
      // state-machine writes (charge emissive, etc.). Base implementation
      // is a no-op; override in subclasses that need per-state visual
      // feedback on the loaded art rather than the hidden procedural mesh.
      this.onModelLoaded(instance);
    } catch (err) {
      // Fallback: keep procedural mesh — model not available.
      // The constructor already tagged rootMesh as "placeholder" so the
      // audit picks it up correctly. But do NOT stay silent (Q-036f):
      // log for devs and surface a once-per-path toast so a player-
      // visible stand-in shape is never an unexplained mystery.
      console.warn(
        `[Interactive ${this.key}] model override failed, using placeholder: ${modelPath}`,
        err,
      );
      if (!InteractiveObject.reportedModelFailures.has(modelPath)) {
        InteractiveObject.reportedModelFailures.add(modelPath);
        gameEvents.emit(GameEvents.HUD_TOAST, {
          message: "Some level art failed to load — showing stand-in shapes.",
        });
      }
    }
  }

  /**
   * Called at the end of a successful `loadModelOverride` with the
   * instantiated glTF model. Default no-op; override in subclasses that
   * need to collect material/mesh refs from the loaded GLB (e.g.
   * CrystalReceiver wires its charge emissive into the loaded
   * `CR_Crystal_*` materials so the pulse reads on final art).
   */
  protected onModelLoaded(_instance: InstantiatedModel): void {
    // no-op — subclasses override
  }

  // ─── Protected helpers ──────────────────────────────────────────────

  protected updateMaterialForState(): void {
    switch (this.state) {
      case "idle":
        this.material.diffuseColor = Color3.FromHexString(COLORS.COPPER);
        this.material.alpha = 0.85;
        this.material.emissiveColor = Color3.Black();
        break;
      case "active":
        this.material.diffuseColor = Color3.FromHexString(COLORS.BRASS);
        this.material.alpha = 1.0;
        this.material.emissiveColor = Color3.FromHexString(COLORS.BRASS).scale(0.15);
        break;
      case "solved":
        this.material.diffuseColor = Color3.FromHexString(COLORS.EMERALD);
        this.material.alpha = 0.9;
        this.material.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.3);
        break;
      case "hidden":
        this.material.diffuseColor = Color3.FromHexString(COLORS.DEEP_PURPLE);
        this.material.alpha = 0.3;
        this.material.emissiveColor = Color3.Black();
        break;
    }
    this.applyReadabilityState();
  }

  protected setChildVisibility(value: number): void {
    const meshes = this.rootMesh.getChildMeshes();
    for (const mesh of meshes) {
      setMeshVisibility(mesh, value);
    }
  }

  private animateVisibility(from: number, to: number, durationMs: number): void {
    const fps = 30;
    const totalFrames = Math.round((durationMs / 1000) * fps);

    const visAnim = new Animation(
      `vis_${this.key}_${Date.now()}`,
      "visibility",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    visAnim.setKeys([
      { frame: 0, value: from },
      { frame: totalFrames, value: to },
    ]);

    const meshes = this.rootMesh.getChildMeshes();
    for (const mesh of meshes) {
      if (!supportsMeshVisibilityAlpha(mesh)) {
        setMeshVisibility(mesh, to);
        continue;
      }
      mesh.animations = [visAnim];
      this.scene.beginAnimation(mesh, 0, totalFrames, false);
    }
  }

  private applyReadabilityState(): void {
    if (this.state === "hidden" && this.readabilityState !== "solved") {
      return;
    }

    const brass = Color3.FromHexString(COLORS.BRASS);
    const copper = Color3.FromHexString(COLORS.COPPER);
    const emerald = Color3.FromHexString(COLORS.EMERALD);

    switch (this.readabilityState) {
      case "suppressed":
        this.material.alpha = Math.min(this.material.alpha, this.state === "solved" ? 0.9 : 0.55);
        if (this.state !== "solved") {
          this.material.emissiveColor = Color3.Black();
        }
        break;
      case "hint":
        this.material.alpha = Math.max(this.material.alpha, 0.88);
        if (this.state !== "solved") {
          this.material.emissiveColor = copper.scale(0.08);
        }
        break;
      case "focus":
        this.material.alpha = 1;
        if (this.state !== "solved") {
          this.material.emissiveColor = brass.scale(0.22);
        }
        break;
      case "active":
        this.material.alpha = 1;
        if (this.state !== "solved") {
          this.material.emissiveColor = brass.scale(0.32);
        }
        break;
      case "solved":
        this.material.diffuseColor = emerald;
        this.material.alpha = 0.95;
        this.material.emissiveColor = emerald.scale(0.35);
        break;
      case "idle":
      default:
        break;
    }
  }
}
